<template>
	<el-container class="favorite-page">
		<el-aside width="230px" class="favorite-aside" :class="{ fullscreen: configStore.fullScreen }">
			<div class="aside-header" :class="configStore.electronMode ? 'header-menu-wrap' : ''">
				<span class="aside-title">{{ $t('favorite.title') }}</span>
			</div>
			<div class="nav-menu">
				<div v-for="item in menuItems" :key="item.key" class="nav-item"
					:class="{ active: currentCategory === item.key }" @click="switchCategory(item.key)">
					<i :class="item.icon"></i>
					<span>{{ item.label }}</span>
				</div>
			</div>
		</el-aside>
		<el-container class="favorite-main">
			<div class="content-header" :class="configStore.electronMode ? 'header-menu-wrap' : ''">
				<div class="header-title">{{ selectMode ? $t('favorite.selectedCount', { n: selectedItems.length }) :
					currentCategoryItem.label }}</div>
			</div>
			<div class="content-body" v-loading="loading" :class="{ 'with-toolbar': selectMode }">
				<no-data-tip v-if="!loading && filteredFavorites.length === 0"></no-data-tip>
				<div v-else class="favorite-list">
					<favorite-message-item v-for="item in filteredFavorites" :key="item.id" :favorite="item"
						:select-mode="selectMode" :selected="isSelected(item)" @click="onClickItem"
						@select-change="onSelectChange" @forward="onForwardMessage" @locate="onLocateMessage"
						@multiSelect="onMultiSelect" @unfavorite="onUnfavorite">
					</favorite-message-item>
				</div>
			</div>
			<div v-if="selectMode" class="multi-select-toolbar">
				<div class="toolbar-actions">
					<el-button size="small" type="primary" :disabled="selectedItems.length === 0"
						@click="onBatchForward">
						{{ $t('chat.forwardOneByOne') }}
					</el-button>
					<el-button size="small" type="danger" :disabled="selectedItems.length === 0"
						@click="onBatchUnfavorite">
						{{ $t('favorite.unfavorite') }}
					</el-button>
					<el-button size="small" @click="exitSelectMode">{{ $t('common.cancel') }}</el-button>
				</div>
			</div>
		</el-container>
		<chat-selector ref="chatSel" :title="$t('chat.forwardMsg')"></chat-selector>
	</el-container>
</template>

<script>
import FavoriteMessageItem from '../components/favorite/FavoriteMessageItem.vue';
import NoDataTip from '../components/common/NoDataTip.vue';
import ChatSelector from '../components/chat/ChatSelector.vue';

export default {
	name: 'FavoritePage',
	components: {
		FavoriteMessageItem,
		NoDataTip,
		ChatSelector
	},
	data() {
		return {
			loading: false,
			favorites: [],
			selectMode: false,
			selectedItems: [],
			currentCategory: 'all'
		}
	},
	computed: {
		menuItems() {
			return [
				{ key: 'all', label: this.$t('favorite.all'), icon: 'icon iconfont icon-fav-all' },
				{ key: 'image', label: this.$t('favorite.image'), icon: 'el-icon-picture-outline' },
				{ key: 'file', label: this.$t('favorite.file'), icon: 'el-icon-document' },
				{ key: 'sticker', label: this.$t('favorite.sticker'), icon: 'icon iconfont icon-emoji' },
				{ key: 'audio', label: this.$t('favorite.audio'), icon: 'el-icon-microphone' },
				{ key: 'video', label: this.$t('favorite.video'), icon: 'el-icon-video-camera' }
			];
		},
		currentCategoryItem() {
			return this.menuItems.find(item => item.key === this.currentCategory) || this.menuItems[0];
		},
		filteredFavorites() {
			if (this.currentCategory === 'all') {
				return this.favorites;
			}
			const typeMap = {
				image: this.$enums.MESSAGE_TYPE.IMAGE,
				file: this.$enums.MESSAGE_TYPE.FILE,
				sticker: this.$enums.MESSAGE_TYPE.STICKER,
				video: this.$enums.MESSAGE_TYPE.VIDEO,
				audio: this.$enums.MESSAGE_TYPE.AUDIO
			};
			const messageType = typeMap[this.currentCategory];
			return this.favorites.filter(item => item.messageType === messageType);
		}
	},
	mounted() {
		this.loadFavorites();
	},
	methods: {
		switchCategory(key) {
			if (this.currentCategory === key) {
				return;
			}
			this.currentCategory = key;
			this.exitSelectMode();
		},
		loadFavorites() {
			this.loading = true;
			this.$http({
				url: '/favorite/list',
				method: 'get'
			}).then(list => {
				this.favorites = list || [];
			}).finally(() => {
				this.loading = false;
			});
		},
		enterSelectMode(favorite) {
			this.selectMode = true;
			this.selectedItems = [];
			if (favorite) {
				this.selectedItems.push(favorite);
			}
		},
		exitSelectMode() {
			this.selectMode = false;
			this.selectedItems = [];
		},
		onMultiSelect(favorite) {
			this.enterSelectMode(favorite);
		},
		isSelected(item) {
			return this.selectedItems.some(f => f.id === item.id);
		},
		onClickItem(item) {
			if (!this.selectMode) {
				return;
			}
			this.toggleSelect(item);
		},
		onSelectChange(item, checked) {
			if (checked) {
				if (!this.isSelected(item)) {
					this.selectedItems.push(item);
				}
			} else {
				this.selectedItems = this.selectedItems.filter(f => f.id !== item.id);
			}
		},
		toggleSelect(item) {
			if (this.isSelected(item)) {
				this.selectedItems = this.selectedItems.filter(f => f.id !== item.id);
			} else {
				this.selectedItems.push(item);
			}
		},
		onForwardMessage(favorite) {
			this.doBatchForward([favorite]);
		},
		async onLocateMessage(favorite) {
			const convKey = this.$db.buildConversationKey(favorite.chatType, favorite.chatId);
			const message = await this.$db.findMessageById(favorite.messageId, convKey);
			if (!message || message.deleted || message.status === this.$enums.MESSAGE_STATUS.RECALL) {
				this.$message.error(this.$t('chat.locateFail'));
				return;
			}
			if (!this.chatStore.conversationMap.get(convKey)) {
				const chatInfo = this.buildChatInfo(favorite);
				if (!chatInfo) {
					this.$message.error(this.$t('chat.locateFail'));
					return;
				}
				await this.chatStore.openChat(chatInfo);
			}
			this.chatStore.setPendingLocateMessage(message);
			await this.chatStore.moveTop(convKey);
			this.chatStore.setActive(convKey);
			this.$router.push('/home/chat');
		},
		buildChatInfo(favorite) {
			if (favorite.chatType === this.$enums.CONVERSATION_TYPE.PRIVATE) {
				const friend = this.friendStore.findFriend(favorite.chatId);
				if (!friend) {
					return null;
				}
				return {
					type: favorite.chatType,
					targetId: favorite.chatId,
					showName: friend.showNickName,
					headImage: friend.headImage,
					companyName: friend.companyName,
					isDnd: friend.isDnd,
					isTop: friend.isTop
				};
			}
			if (favorite.chatType === this.$enums.CONVERSATION_TYPE.GROUP) {
				const group = this.groupStore.findGroup(favorite.chatId);
				if (!group) {
					return null;
				}
				return {
					type: favorite.chatType,
					targetId: favorite.chatId,
					showName: group.showGroupName,
					headImage: group.headImageThumb,
					isDnd: group.isDnd,
					isTop: group.isTop
				};
			}
			return null;
		},
		onBatchForward() {
			if (this.selectedItems.length === 0) {
				this.$message.warning(this.$t('favorite.selectToForward'));
				return;
			}
			this.doBatchForward(this.selectedItems);
		},
		async doBatchForward(forwardableMessages) {
			if (forwardableMessages.length === 0) {
				this.$message.warning(this.$t('chat.noForwardMsg'));
				return;
			}
			this.$refs.chatSel.open(async (conversations) => {
				this.exitSelectMode();
				let successCount = 0;
				let totalCount = conversations.length * forwardableMessages.length;
				let completedCount = 0;
				for (const conv of conversations) {
					for (const forwardMessage of forwardableMessages) {
						try {
							const message = {
								localId: this.$nextSnowflakeId(),
								content: forwardMessage.content,
								type: forwardMessage.messageType
							};
							if (conv.type == this.$enums.CONVERSATION_TYPE.GROUP) {
								message.groupId = conv.targetId;
							} else {
								message.recvId = conv.targetId;
							}
							const m = await this.$http({
								url: `/message/${this.chatTypeText(conv)}/send`,
								method: 'post',
								data: message
							});
							m.selfSend = true;
							this.chatStore.openChat(conv);
							this.chatStore.insertMessage(conv.key, m);
							successCount++;
						} finally {
							completedCount++;
							if (completedCount === totalCount) {
								if (successCount === totalCount) {
									this.$message.success(this.$t('chat.forwardSuccessToChats', {
										n: forwardableMessages.length,
										m: conversations.length
									}));
								} else {
									this.$message.warning(this.$t('chat.forwardPartialFail', {
										success: successCount,
										total: totalCount
									}));
								}
							}
						}
					}
				}
			});
		},
		onUnfavorite(favorite) {
			this.$confirm(this.$t('favorite.unfavoriteConfirm'), this.$t('favorite.unfavoriteTitle'), {
				confirmButtonText: this.$t('common.confirm'),
				cancelButtonText: this.$t('common.cancel'),
				type: 'warning'
			}).then(() => {
				this.deleteFavorites([favorite.id]);
			});
		},
		onBatchUnfavorite() {
			if (this.selectedItems.length === 0) {
				this.$message.warning(this.$t('favorite.selectToUnfavorite'));
				return;
			}
			this.$confirm(this.$t('favorite.batchUnfavoriteConfirm', { n: this.selectedItems.length }),
				this.$t('favorite.unfavoriteTitle'), {
				confirmButtonText: this.$t('common.confirm'),
				cancelButtonText: this.$t('common.cancel'),
				type: 'warning'
			}).then(() => {
				const ids = this.selectedItems.map(item => item.id);
				this.deleteFavorites(ids);
			});
		},
		deleteFavorites(ids) {
			const tasks = ids.map(id => this.$http({
				url: `/favorite/delete/${id}`,
				method: 'delete'
			}));
			Promise.all(tasks).then(() => {
				this.favorites = this.favorites.filter(item => !ids.includes(item.id));
				this.selectedItems = this.selectedItems.filter(item => !ids.includes(item.id));
				if (this.selectedItems.length === 0) {
					this.exitSelectMode();
				}
				this.$message.success(this.$t('chat.deleteSuccess'));
			});
		},
		chatTypeText(conv) {
			return conv.type == this.$enums.CONVERSATION_TYPE.PRIVATE ? 'private' : 'group';
		}
	}
}
</script>

<style lang="scss" scoped>
.favorite-page {
	height: 100vh;
	background: #f5f6fa;

	.favorite-aside {
		display: flex;
		flex-direction: column;
		height: 100%;
		border-right: 1px solid rgba(0, 0, 0, 0.08);
		box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05);
		background: white;

		&.fullscreen {
			width: 260px !important;

			@media (min-width: 1200px) {
				width: 290px !important;
			}
		}

		.aside-header {
			height: 60px;
			flex-shrink: 0;
			display: flex;
			align-items: center;
			padding: 0 20px;
			border-bottom: var(--im-border);

			.aside-title {
				font-size: var(--im-font-size-larger);
				font-weight: 600;
				color: #333;
			}
		}

		.nav-menu {
			flex: 1;
			overflow-y: auto;
			padding: 5px 0;

			.nav-item {
				display: flex;
				align-items: center;
				gap: 10px;
				height: 50px;
				margin: 0 3px;
				padding: 5px 12px;
				cursor: pointer;
				border-radius: 10px;
				white-space: nowrap;

				&:hover {
					background-color: var(--im-background-active);
				}

				&.active {
					background-color: var(--im-background-active-dark);
				}

				i {
					font-size: 18px;
					width: 20px;
					text-align: center;
				}

				span {
					font-size: var(--im-font-size);
				}
			}
		}
	}

	.favorite-main {
		display: flex;
		flex-direction: column;
		background: #fcfdff;
		overflow: hidden;
		position: relative;

		.content-header {
			height: 60px;
			flex-shrink: 0;
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 0 20px;
			border-bottom: var(--im-border);
			background: white;

			.header-title {
				font-size: var(--im-font-size-larger);
				font-weight: 600;
				color: #333;
			}
		}

		.content-body {
			flex: 1;
			overflow-y: auto;
			padding: 16px 20px;
			background: #fafbfc;
			min-height: 0;

			&.with-toolbar {
				padding-bottom: 80px;
			}
		}

		.favorite-list {
			display: flex;
			flex-direction: column;
			gap: 12px;
			min-height: 200px;
		}

		.multi-select-toolbar {
			position: absolute;
			bottom: 24px;
			left: 50%;
			transform: translateX(-50%);
			background: white;
			border-radius: 8px;
			box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
			padding: 12px 20px;
			z-index: 100;
			border: 1px solid rgba(0, 0, 0, 0.1);

			.toolbar-actions {
				display: flex;
				gap: 8px;
			}
		}
	}
}
</style>
